import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Play, Loader2, Film, Sparkles } from "lucide-react";

/**
 * The 30-second cinematic "how Draftwave works" film for the Landing hero.
 *
 * Polls /api/homepage-video for status; renders one of three states:
 *   - ready: full-width theatrical video player with poster + play button
 *   - rendering: cinematic "on air" placeholder with live render progress
 *   - not_started / failed: elegant fallback with an animated grid of scenes
 */
export default function HomepageVideo() {
  const [state, setState] = useState({ status: "loading" });
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let mounted = true;
    let timer;

    async function tick() {
      try {
        const r = await api.get("/homepage-video");
        if (!mounted) return;
        setState(r.data || { status: "not_started" });
        const s = r.data?.status;
        // Poll while rendering so viewers see the progress bar move
        if (s === "rendering") timer = setTimeout(tick, 5000);
      } catch {
        if (mounted) setState({ status: "not_started" });
      }
    }
    tick();
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const videoUrl = useMemo(() => {
    if (state?.status !== "ready" || !state?.url) return null;
    return `${BACKEND_URL}${state.url}`;
  }, [state]);

  const posterUrl = useMemo(() => {
    if (!state?.poster_url) return null;
    return `${BACKEND_URL}${state.poster_url}`;
  }, [state]);

  function play() {
    if (!videoRef.current) return;
    videoRef.current.play().catch(() => {});
    setPlaying(true);
  }

  return (
    <section
      className="border-b border-border relative overflow-hidden bg-foreground text-background"
      data-testid="homepage-video-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid md:grid-cols-[280px_1fr] gap-12 items-start">
          <div>
            <div className="overline text-primary mb-4 flex items-center gap-2">
              <Film className="w-4 h-4" /> Watch — 30 seconds
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-none tracking-tight">
              How <span className="italic text-primary">Draftwave</span> works.
            </h2>
            <p className="mt-6 text-sm md:text-base text-background/70 leading-relaxed">
              A 30-second cinematic tour of the workspace — composed by our own agents.
              Sora 2 shot every frame. Draftwave Nova ∞ directed. Onyx narrates.
            </p>
            <div className="mt-6 flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-background/60">
              <Sparkles className="w-3.5 h-3.5" /> Made in Draftwave
            </div>
          </div>

          <div className="relative">
            <div
              className="relative aspect-video w-full border border-background/20 bg-black overflow-hidden"
              data-testid="homepage-video-frame"
            >
              {videoUrl ? (
                <>
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    poster={posterUrl || undefined}
                    playsInline
                    preload="metadata"
                    controls={playing}
                    className="w-full h-full object-cover"
                    data-testid="homepage-video-player"
                    onEnded={() => setPlaying(false)}
                  />
                  {!playing && (
                    <button
                      type="button"
                      onClick={play}
                      className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/40 transition group"
                      aria-label="Play the 30-second Draftwave film"
                      data-testid="homepage-video-play-btn"
                    >
                      <span className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
                        <Play className="w-8 h-8 ml-1 fill-current" />
                      </span>
                    </button>
                  )}
                </>
              ) : state?.status === "rendering" ? (
                <RenderingSlate progress={state.progress_pct} msg={state.progress_msg} />
              ) : state?.status === "failed" ? (
                <FallbackSlate msg="Cinematic is warming up — check back shortly." />
              ) : (
                <FallbackSlate msg="Your Draftwave cinematic is being staged." />
              )}
            </div>
            <div className="mt-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-background/50">
              <span data-testid="homepage-video-status-label">
                {state?.status === "ready"
                  ? "30 sec · Cinematic · Sora 2 · TTS-HD"
                  : state?.status === "rendering"
                  ? `Rendering · ${state.progress_pct || 0}%`
                  : "Coming online"}
              </span>
              <span>DRAFTWAVE ORIGINAL</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RenderingSlate({ progress = 0, msg }) {
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-black via-[#111] to-black text-background"
      data-testid="homepage-video-rendering"
    >
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
      <div className="relative flex items-center gap-3 text-primary">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="overline text-xs">Directing your cinematic…</span>
      </div>
      <div className="relative font-serif-display text-3xl md:text-5xl text-center px-6">
        A trillion starts with <span className="italic text-primary">a draft.</span>
      </div>
      <div className="relative text-xs text-background/60 max-w-md text-center px-6">
        {msg || "Sora 2, Draftwave Nova ∞ and TTS-HD are composing your 30s film."}
      </div>
      <div className="relative w-64 max-w-[80%] h-1.5 bg-background/10 overflow-hidden">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${Math.min(100, Math.max(2, progress))}%` }}
        />
      </div>
      <div className="relative text-[10px] font-mono uppercase tracking-widest text-background/40">
        {progress}%
      </div>
    </div>
  );
}

function FallbackSlate({ msg }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-black via-[#0f0f1a] to-black text-background gap-4">
      <Film className="w-8 h-8 text-primary" />
      <div className="font-serif-display text-2xl text-center px-6">{msg}</div>
    </div>
  );
}
